import React from 'react';
import { ChatText, UserCircle } from '@phosphor-icons/react';
import { useAuthOptional } from '../../contexts/AuthContext';
import { useFeedbackModalOptional } from '../../contexts/FeedbackModalContext';

export type EngagementStripVariant = 'compact' | 'card';

export interface EngagementStripProps {
  feedbackSource: string;
  feedbackMetadata?: Record<string, unknown>;
  variant?: EngagementStripVariant;
  className?: string;
}

/**
 * Reusable sign-in + feedback strip for guests (sign-in only when logged out).
 * Feedback is offered for everyone when a feedback modal provider is present.
 *
 * Does not depend on IntlProvider — safe to render in layouts without it
 * (e.g. ResourcePageLayout). Strings use their English defaults directly.
 */
export function EngagementStrip({
  feedbackSource,
  feedbackMetadata,
  variant = 'card',
  className = '',
}: EngagementStripProps) {
  const auth = useAuthOptional();
  const feedback = useFeedbackModalOptional();

  const isAuthenticated = auth?.isAuthenticated === true;
  const signIn = auth?.signIn;
  const canFeedback = feedback != null;
  const showSignIn = !isAuthenticated && Boolean(signIn);
  const showFeedback = canFeedback;

  if (!showSignIn && !showFeedback) {
    return null;
  }

  const isCompact = variant === 'compact';
  const shell = isCompact
    ? 'rounded-xl border border-[#DDD5C8]/80 dark:border-[#4A3520]/80 bg-[#F8F5F0]/90 dark:bg-[#170F07]/80 px-3 py-2.5'
    : 'rounded-2xl border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] p-4 sm:p-5 shadow-sm dark:shadow-none';

  return (
    <div
      className={`${shell} ${className}`}
      role="region"
      aria-label="Account and feedback"
    >
      <div
        className={
          isCompact
            ? 'flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-center gap-2 sm:gap-3 text-center'
            : 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'
        }
      >
        <p
          className={
            isCompact
              ? 'text-xs text-[#5A4A3A] dark:text-[#B8A48C]'
              : 'text-sm text-[#3D3529] dark:text-[#C4A882] max-w-prose'
          }
        >
          {isAuthenticated
            ? 'Spot something wrong or have an idea? We read every message.'
            : 'Help us improve JobCrawls — sign in to sync saved jobs across devices, or send feedback anytime.'}
        </p>
        <div className={`flex flex-wrap items-center gap-2 ${isCompact ? 'justify-center' : 'sm:justify-end'}`}>
          {showSignIn && signIn ? (
            <button
              type="button"
              onClick={() => signIn()}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#3B5E45] px-4 py-2 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-[#2F4A38] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#98CFA8] dark:shadow-none cursor-pointer"
            >
              <UserCircle className="h-4 w-4 shrink-0" />
              Sign in
            </button>
          ) : null}
          {showFeedback ? (
            <button
              type="button"
              onClick={() => feedback!.openFeedback({ source: feedbackSource, metadata: feedbackMetadata })}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#DDD5C8] bg-white px-4 py-2 text-xs sm:text-sm font-medium text-[#3D3529] transition hover:bg-[#F2EADF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B5E45]/40 dark:border-[#4A3520] dark:bg-[#2A1D0F] dark:text-[#C4A882] dark:hover:bg-[#342415] dark:focus-visible:ring-[#98CFA8]/40 cursor-pointer"
            >
              <ChatText className="h-4 w-4 shrink-0" />
              Send feedback
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
